import * as React from "react"
import { createPortal } from "react-dom"

import {
  IsiBody,
  IsiDivider,
  IsiHeading,
  IsiInlineShell,
  IsiLead,
  IsiLink,
  IsiList,
  IsiSheetShell,
  IsiSup,
  IsiTrayShell,
  IsiWarning,
} from "./index"

const combinedOralContraceptiveLinks = [
  {
    href: "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=61207a91-8b3c-4231-b98f-3cb9f4a4be09&audience=consumer",
    label: "Aubra EQ",
  },
  {
    href: "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=4c59652c-49a6-4eed-b557-9abd3ffac541&audience=consumer",
    label: "Chateal EQ",
  },
  {
    href: "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=2417aee4-6838-4ea6-8e0d-3144f522310c&audience=consumer",
    label: "Cyred EQ",
  },
  {
    href: "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=ed7dd100-a339-41bc-91be-6d20b73dbce6&audience=consumer",
    label: "Tarina Fe 1/20 EQ",
  },
  {
    href: "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=557fa655-c40f-4ecb-bd18-4820c7e15e4b&audience=consumer",
    label: "Tarina 24 Fe",
  },
  {
    href: "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=f59acc3e-21d9-4fa3-ad52-d77225739e1a&audience=consumer",
    label: "Tri-VyLibra",
  },
  {
    href: "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=bd51a08e-cd45-4315-9bca-41794450ef16&audience=consumer",
    label: "Tri-VyLibra Lo",
  },
  {
    href: "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=13bdc226-ecf8-4fd7-aa0e-ec19b6b74fc3&audience=consumer",
    label: "VyLibra",
  },
] as const

const combinedOralContraceptiveBullets = [
  "smoke and are over 35 years of age",
  "had blood clots in your arms, legs, lungs, or eyes",
  "had a problem with your blood that makes it clot more than normal",
  "have certain heart valve problems or irregular heartbeat that increases your risk of blood clots",
  "had a stroke",
  "had a heart attack",
  "have high blood pressure that cannot be controlled by medicine",
  "have diabetes with kidney, eye, nerve, or blood vessel damage",
  "have certain kinds of severe migraine headaches with aura, numbness, weakness, or changes in vision, or any migraine headaches if you are over 35 years of age",
  "have liver problems, including liver tumors",
  "take any hepatitis C drug combination containing ombitasvir/paritaprevir/ritonavir, with or without dasabuvir (may increase levels of the liver enzyme alanine aminotransferase (ALT) in the blood)",
  "have any unexplained vaginal bleeding",
  "had breast cancer or any cancer that is sensitive to female hormones",
] as const

const jasmielBullets = [
  "Ever had blood clots in your legs (deep vein thrombosis), lungs (pulmonary embolism), or eyes (retinal thrombosis)",
  "Ever had a stroke",
  "Ever had a heart attack",
  "Have certain heart valve problems or heart rhythm abnormalities that can cause blood clots to form in the heart",
  "Have an inherited problem with your blood that makes it clot more than normal",
  "Have high blood pressure that medicine can't control",
  "Have diabetes with kidney, eye, nerve, or blood vessel damage",
  "Ever had certain kinds of severe migraine headaches with aura, numbness, weakness, or changes in vision",
  "Ever had breast cancer, which may be sensitive to female hormones",
  "Have liver disease, including liver tumors",
  "Take any hepatitis C drug combination containing ombitasvir/paritaprevir/ritonavir, with or without dasabuvir (may increase levels of the liver enzyme alanine aminotransferase (ALT) in the blood)",
  "Have kidney disease",
  "Have adrenal disease",
  "Smoke and are over 35 years old",
  "Are pregnant or suspect you are pregnant",
] as const

const lyleqBullets = [
  "there is any chance you may be pregnant",
  "you have breast cancer",
  "you have bleeding between your periods that has not been diagnosed",
  "you are taking certain drugs for epilepsy (seizures) or for tuberculosis",
  "you are hypersensitive or allergic to any component of Lyleq",
  "you have liver tumors, either benign or cancerous",
  "you have acute liver disease",
] as const

const sheetTransitionMs = 300
const jasmielPatientInfoHref =
  "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=1a3211f8-41f5-4905-a466-0d79add5fdad&audience=consumer"
const lyleqPatientInfoHref =
  "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=c9f00a3e-c827-4066-bf85-079b3a20f25e&audience=consumer"
const econtraPatientInfoHref =
  "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=94b23c49-dfba-4a70-ada5-e5c212e703c5&audience=consumer"

function ConsumerIsiPreview() {
  return (
    <div className="flex flex-col gap-4">
      <p className="font-sans text-isi font-bold text-isi-copy">
        PLEASE SEE FULL PRESCRIBING INFORMATION FOR EACH PRODUCT, INCLUDING
        BOXED WARNINGS.
      </p>
      <p className="font-sans text-isi text-isi-copy">
        <strong className="font-bold">
          IMPORTANT RISK INFORMATION for combined oral contraceptives, including
          Aubra EQ{`\u00AE`}{" "}
        </strong>
        <span>
          (levonorgestrel and ethinyl estradiol tablets, USP 0.1 mg/0.02 mg),
        </span>{" "}
        <strong className="font-bold">Chateal EQ{`\u00AE`} </strong>
        <span>
          (levonorgestrel and ethinyl estradiol tablets, USP 0.15 mg/0.03 mg),{" "}
        </span>
        <strong className="font-bold">Cyred EQ{`\u00AE`} </strong>
        <span>
          (desogestrel and ethinyl estradiol tablets, USP 0.15 mg/0.03 mg),
        </span>{" "}
        <strong className="font-bold">Tarina Fe 1/20 EQ{`\u00AE`} </strong>
        <span>
          (norethindrone acetate and ethinyl estradiol tablets, USP and ferrous
          fumarate tablets, 1 mg/20 mcg and 75 mg),{" "}
        </span>
        <strong className="font-bold">Tarina{`\u00AE`} 24 Fe </strong>
        <span>
          (norethindrone acetate and ethinyl estradiol tablets, USP and ferrous
          fumarate tablets, 1 mg/20 mcg and 75 mg),
        </span>
      </p>
    </div>
  )
}

function CombinedOralContraceptiveSection() {
  return (
    <div className="flex flex-col gap-4">
      <IsiBody>
        <span className="font-bold text-isi-link">
          IMPORTANT RISK INFORMATION{" "}
        </span>
        <span className="font-bold">
          for combined oral contraceptives, including Aubra EQ{`\u00AE`}
        </span>{" "}
        <span>(levonorgestrel and ethinyl estradiol tablets, USP 0.1 mg/0.02 mg),</span>
        <span className="font-bold"> Chateal EQ{`\u00AE`} </span>
        <span>
          (levonorgestrel and ethinyl estradiol tablets, USP 0.15 mg/0.03 mg),{" "}
        </span>
        <span className="font-bold">Cyred EQ{`\u00AE`} </span>
        <span>(desogestrel and ethinyl estradiol tablets, USP 0.15 mg/0.03 mg),</span>
        <span className="font-bold"> Tarina Fe 1/20 EQ{`\u00AE`} </span>
        <span>
          (norethindrone acetate and ethinyl estradiol tablets, USP and ferrous
          fumarate tablets, 1 mg/20 mcg and 75 mg),
        </span>
        <span className="font-bold"> Tarina{`\u00AE`} 24 Fe </span>
        <span>
          (norethindrone acetate and ethinyl estradiol tablets, USP and ferrous
          fumarate tablets, 1 mg/20 mcg and 75 mg),
        </span>
        <span className="font-bold"> Tri-VyLibra{`\u00AE`} </span>
        <span>
          (norgestimate and ethinyl estradiol tablets, USP 0.180 mg/0.035 mg,
          0.215 mg/0.035 mg, and 0.250 mg/0.035 mg),
        </span>{" "}
        <span className="font-bold">
          Tri-VyLibra{`\u00AE`} Lo{" "}
        </span>
        <span>
          (norgestimate and ethinyl estradiol tablets, USP 0.180 mg/0.025 mg,
          0.215 mg/0.025 mg, and 0.250 mg/0.025 mg),
        </span>{" "}
        <span>and</span>
        <span className="font-bold"> VyLibra{`\u00AE`}</span>
        <span>
          {" "}
          (norgestimate and ethinyl estradiol tablets, USP 0.250 mg/0.035 mg).
        </span>
      </IsiBody>

      <IsiWarning>
        WARNING: Do not use combined oral contraceptives (COCs) including Aubra
        EQ, Chateal EQ, Cyred EQ, Tarina Fe 1/20 EQ, Tarina 24 Fe, Tri-VyLibra,
        Tri-VyLibra Lo, and VyLibra if you smoke cigarettes and are over 35
        years old. Smoking increases your risk of serious cardiovascular side
        effects (heart and blood vessel problems) from COCs, including death
        from heart attack, blood clots, or stroke. This risk increases with age
        and the number of cigarettes you smoke.
      </IsiWarning>

      <IsiLead>
        Please review the full Prescribing Information, including the Boxed
        Warning, and Patient Information for the combined oral contraceptives
        below:
      </IsiLead>

      <div className="flex flex-col gap-0.5">
        {combinedOralContraceptiveLinks.map(link => (
          <IsiLink href={link.href} key={link.label}>
            {link.label}
          </IsiLink>
        ))}
      </div>

      <IsiHeading>What are combined oral contraceptives (COCs) used for?</IsiHeading>
      <IsiBody>
        COCs are prescription birth control pills used to prevent pregnancy.
      </IsiBody>

      <IsiHeading>How effective are COCs?</IsiHeading>
      <IsiBody>
        COCs are taken to prevent pregnancy, and when taken correctly, have a
        failure rate of approximately 1% per year (1 pregnancy per 100 women
        per year of use) when used without missing any pills. The average
        failure rate of large numbers of pill users is approximately 5% per
        year (5 pregnancies per 100 women per year of use) when women who miss
        pills are included. For most women oral contraceptives are also free of
        serious or unpleasant side effects. However, forgetting to take pills
        considerably increases the chances of pregnancy.
      </IsiBody>
      <IsiBody>
        The effectiveness of Tarina 24 Fe to prevent pregnancy in women who are
        obese with a body mass index more than 35 kg/m
        <IsiSup>2</IsiSup> has not been studied.
      </IsiBody>

      <IsiHeading>
        Do some COCs have other uses in addition to preventing pregnancy?
      </IsiHeading>
      <IsiBody>
        Yes. Tri-VyLibra is used for the treatment of moderate acne in women
        who are at least 15 years old who have started having periods, don't
        have a medical reason to avoid COCs, and want to use a COC for birth
        control.
      </IsiBody>

      <IsiHeading>Who should not take COCs?</IsiHeading>
      <div className="flex flex-col gap-0">
        <IsiBody>Do not take COCs if you:</IsiBody>
        <IsiList>
          {combinedOralContraceptiveBullets.map(item => (
            <li key={item}>{item}</li>
          ))}
        </IsiList>
      </div>

      <IsiHeading>What else should I know about taking COCs?</IsiHeading>
      <IsiBody>
        Stop taking COCs if you have a blood clot, sudden vision problems,
        headaches with vision changes, or yellowing of the skin or eyes. Stop
        taking COCs at least 4 weeks before you have major surgery and do not
        restart after the surgery without asking your healthcare provider. Be
        sure to use other forms of birth control (like condoms or spermicide)
        during this time period. You should not take COCs any earlier than 4
        weeks after having a baby or if you are breastfeeding. If you are
        pre-diabetic or diabetic, your healthcare provider should monitor you
        while taking COCs. Your healthcare provider should evaluate any
        significant changes in headaches and irregular period bleeding.
      </IsiBody>

      <IsiHeading>What are the most serious risks of taking COCs?</IsiHeading>
      <IsiBody>
        COCs increase the risk of serious conditions, including blood clots,
        stroke, and heart attack. These serious conditions can cause permanent
        disability or death. Other possible serious risks include liver tumors,
        high blood pressure, and gallbladder problems. It is not known if
        hormonal birth control pills cause breast cancer. Some studies, but not
        all, suggest that there could be a slight increase in the risk of
        breast cancer among current users with longer duration of use.
      </IsiBody>

      <IsiHeading>What are possible side effects of COCs?</IsiHeading>
      <IsiBody>
        The most common side effects reported by women taking COCs include
        acne, nausea/vomiting, bleeding changes, weight changes, breast
        tenderness, vaginal infections, difficulty wearing contact lenses,
        headaches, anxiety, and depression.
      </IsiBody>

      <IsiLead>
        COCs are intended to prevent pregnancy. COCs do not protect against
        transmission of HIV (AIDS) and other sexually transmitted diseases
        (STDs), such as chlamydia, genital herpes, genital warts, gonorrhea,
        hepatitis B, and syphilis.
      </IsiLead>
    </div>
  )
}

function JasmielSection() {
  return (
    <div className="flex flex-col gap-4">
      <IsiHeading>
        IMPORTANT RISK INFORMATION for Jasmiel{`\u00AE`} (drospirenone and
        ethinyl estradiol tablets, USP 3 mg/0.02 mg)
      </IsiHeading>

      <IsiWarning>
        <>
          <span className="block">WARNING TO WOMEN WHO SMOKE</span>
          <span>
            Do not use Jasmiel if you smoke cigarettes and are over 35 years
            old. Smoking increases your risk of serious cardiovascular side
            effects (heart and blood vessel problems) from birth control pills,
            including death from heart attack, blood clots or stroke. This risk
            increases with age and the number of cigarettes you smoke.
          </span>
        </>
      </IsiWarning>

      <IsiBody>
        <strong className="font-bold">
          Please review the full Prescribing Information, including the Boxed
          Warning, and Patient Information{" "}
        </strong>
        <IsiLink href={jasmielPatientInfoHref}>here</IsiLink>
        <strong className="font-bold text-isi-link">.</strong>
      </IsiBody>

      <IsiHeading>What is Jasmiel?</IsiHeading>
      <IsiBody>
        Jasmiel is a prescription birth control pill used to prevent pregnancy.
        It is also used to treat symptoms of premenstrual dysphoric disorder
        (PMDD) if you choose to use the pill for birth control. It is also used
        to treat acne in women who are at least 14 years old, have started
        having periods, and choose to use the pill for birth control. Jasmiel
        has not been evaluated for the treatment of premenstrual syndrome
        (PMS).
      </IsiBody>

      <IsiHeading>Who should not take Jasmiel?</IsiHeading>
      <div className="flex flex-col gap-0">
        <IsiBody>Your healthcare provider will not give you Jasmiel if you:</IsiBody>
        <IsiList>
          {jasmielBullets.map(item => (
            <li key={item}>{item}</li>
          ))}
        </IsiList>
      </div>

      <IsiHeading>What else should I know about taking Jasmiel?</IsiHeading>
      <IsiBody>
        Tell your healthcare provider if you take any daily medicines that can
        increase potassium levels, such as ibuprofen, diuretics, potassium
        supplements, ACE inhibitors, angiotensin-II receptor antagonists,
        heparin, or aldosterone antagonists. Stop taking Jasmiel if you have a
        blood clot, sudden vision problems, headaches with vision changes, or
        yellowing of the skin or eyes. Stop taking Jasmiel at least 4 weeks
        before major surgery; wait until at least 2 weeks after major surgery
        to restart Jasmiel. You should not take Jasmiel any earlier than 4
        weeks after having a baby or if you are breastfeeding. If you are
        pre-diabetic or diabetic, your healthcare provider should monitor you
        while taking Jasmiel. Your healthcare provider should evaluate any
        significant changes in headaches and irregular bleeding.
      </IsiBody>

      <IsiHeading>What are the most serious risks of taking Jasmiel?</IsiHeading>
      <IsiBody>
        Like pregnancy, birth control pills increase the risk of serious blood
        clots, especially in women who have other risk factors, such as
        smoking, obesity, or age greater than 35 years. Women who use birth
        control pills with drospirenone (like Jasmiel) may have a higher risk
        of getting a blood clot. Other possible serious risks include high
        blood pressure, gallbladder problems, and liver tumors. It is not known
        if hormonal birth control pills cause breast cancer. Some studies, but
        not all, suggest that there could be a slight increase in the risk of
        breast cancer among current users with longer duration of use.
      </IsiBody>

      <IsiHeading>What are possible side effects of Jasmiel?</IsiHeading>
      <IsiBody>
        The most common side effects reported by women taking birth control
        pills like Jasmiel include nausea, headache, bleeding changes, and
        breast tenderness.
      </IsiBody>

      <IsiLead>
        Jasmiel is intended to prevent pregnancy. Birth control pills like
        Jasmiel do not protect you against HIV infection (AIDS) and other
        sexually transmitted diseases (STDs).
      </IsiLead>
    </div>
  )
}

function LyleqSection() {
  return (
    <div className="flex flex-col gap-4">
      <IsiHeading>
        IMPORTANT RISK INFORMATION for Lyleq{`\u00AE`} (norethindrone tablets,
        USP 0.35 mg)
      </IsiHeading>

      <IsiWarning>
        WARNING: Cigarette smoking greatly increases the possibility of
        suffering heart attacks and strokes. Women who use oral contraceptives
        are strongly advised not to smoke.
      </IsiWarning>

      <IsiBody>
        <strong className="font-bold">
          Please review the full Prescribing Information, including the Boxed
          Warning, and Patient Information{" "}
        </strong>
        <IsiLink href={lyleqPatientInfoHref}>here</IsiLink>
        <strong className="font-bold text-isi-link">.</strong>
      </IsiBody>

      <IsiHeading>What is Lyleq?</IsiHeading>
      <IsiBody>
        Lyleq is a prescription birth control pill used to prevent pregnancy.
      </IsiBody>

      <IsiHeading>Who should not take Lyleq?</IsiHeading>
      <div className="flex flex-col gap-0">
        <IsiBody>You should not take Lyleq if:</IsiBody>
        <IsiList>
          {lyleqBullets.map(item => (
            <li key={item}>{item}</li>
          ))}
        </IsiList>
      </div>

      <IsiHeading>What else should I know about taking Lyleq?</IsiHeading>
      <IsiBody>
        Stop taking Lyleq if you have new or worsening headaches. If you are
        pre-diabetic or diabetic, your healthcare provider should monitor you
        while taking Lyleq. You must take Lyleq at the same time every day or
        it may not protect against pregnancy. Tell your healthcare provider if
        you have belly pain while taking Lyleq, as this could be a sign of
        tubal pregnancy or cysts on the ovaries. Tell your healthcare provider
        if you take other daily medications, such as seizure medication,
        antibiotics, anti-HIV medications, or herbal products because
        sometimes these products make Lyleq less effective in preventing
        pregnancy.
      </IsiBody>

      <IsiHeading>What are the most serious risks of taking Lyleq?</IsiHeading>
      <IsiBody>
        Lyleq can increase the risk of serious conditions including tubal
        pregnancy and ovarian cysts.
      </IsiBody>

      <IsiHeading>What are possible side effects of Lyleq?</IsiHeading>
      <IsiBody>
        The most common side effects reported by women taking Lyleq include
        nausea, headache, bleeding changes, dizziness, and breast tenderness.
        Rarely, acne, weight gain, and unusual hair growth can occur.
      </IsiBody>

      <IsiLead>
        Lyleq is intended to prevent pregnancy. Birth control pills like Lyleq
        do not protect against transmission of HIV (AIDS) and other sexually
        transmitted diseases (STDs) such as chlamydia, genital herpes, genital
        warts, gonorrhea, hepatitis B, and syphilis.
      </IsiLead>
    </div>
  )
}

function EContraSection() {
  return (
    <div className="flex flex-col gap-4">
      <IsiHeading>
        PRODUCT INFORMATION for EContra One-Step{`\u00AE`} (levonorgestrel
        tablet, 1.5 mg)
      </IsiHeading>

      <IsiBody>
        <strong className="font-bold">
          Please review the full Prescribing Information and Patient
          Information{" "}
        </strong>
        <IsiLink href={econtraPatientInfoHref}>here</IsiLink>
        <strong className="font-bold text-isi-link">.</strong>
      </IsiBody>

      <IsiHeading>What is EContra One-Step?</IsiHeading>
      <IsiBody>
        EContra One-Step is emergency contraception that helps prevent
        pregnancy after regular birth control failure or unprotected sex. It is
        a backup method of preventing pregnancy and should not be used as
        regular birth control.
      </IsiBody>

      <IsiHeading>What EContra One-Step is not:</IsiHeading>
      <IsiBody>
        EContra One-Step will not work if you are already pregnant and will not
        affect an existing pregnancy.
      </IsiBody>

      <IsiHeading>When should I use EContra One-Step?</IsiHeading>
      <IsiBody>
        The sooner you take emergency contraception, the better it works. You
        should use EContra One-Step within 72 hours (3 days) after you have had
        unprotected sex. EContra One-Step is a backup or emergency method of
        birth control you can use when your regular birth control was used
        incorrectly or failed, or when you did not use any birth control.
      </IsiBody>

      <IsiHeading>When should I not use EContra One-Step?</IsiHeading>
      <IsiBody>
        EContra One-Step should not be used as a regular birth control method
        because it's not as effective as regular birth control. EContra
        One-Step should not be used if you are already pregnant because it will
        not work. EContra One-Step should not be used if you are allergic to
        levonorgestrel or any other ingredients in EContra One-Step.
      </IsiBody>

      <IsiHeading>What else should I know about taking EContra One-Step?</IsiHeading>
      <IsiBody>
        Ask a healthcare provider or pharmacist before using EContra One-Step
        if you are taking efavirenz (HIV medication), rifampin (tuberculosis
        treatment), or medication for seizures (epilepsy). These medications
        may reduce the effectiveness of EContra One-Step and increase your
        chance of becoming pregnant. Your healthcare provider may prescribe
        another form of emergency contraception that may not be affected by
        these medications.
      </IsiBody>

      <IsiHeading>What are possible side effects of EContra One-Step?</IsiHeading>
      <IsiBody>
        Commonly reported adverse reactions include period changes, abdominal
        pain, nausea/vomiting, tiredness, headache, dizziness, and breast pain.
        Contact your healthcare provider if you have severe abdominal pain.
      </IsiBody>

      <IsiLead>
        EContra One-Step will not protect you from HIV infection (AIDS) and
        other sexually transmitted diseases (STDs).
      </IsiLead>
    </div>
  )
}

function ConsumerIsiContent() {
  return (
    <div className="flex flex-col gap-4">
      <IsiLead>
        PLEASE SEE FULL PRESCRIBING INFORMATION FOR EACH PRODUCT, INCLUDING
        BOXED WARNINGS.
      </IsiLead>

      <CombinedOralContraceptiveSection />
      <IsiDivider />
      <JasmielSection />
      <IsiDivider />
      <LyleqSection />
      <IsiDivider />
      <EContraSection />
      <IsiDivider />

      <IsiBody>
        You can report <strong className="font-bold">SUSPECTED ADVERSE REACTIONS</strong>{" "}
        by calling 1-855-888-2467 or reporting via the FDA MedWatch Program at{" "}
        <IsiLink href="https://www.fda.gov/medwatch">www.fda.gov/medwatch</IsiLink>
        <span className="hidden md:inline">
          {" "}
          <br aria-hidden="true" />
        </span>{" "}
        or 1-800-FDA-1088.
      </IsiBody>
    </div>
  )
}

function useIsiTrayVisibility(
  inlineRegionRef: React.RefObject<HTMLElement>,
  trayEnabled: boolean,
) {
  const [isTrayVisible, setIsTrayVisible] = React.useState(false)

  const syncVisibility = React.useCallback(() => {
    if (!trayEnabled) {
      setIsTrayVisible(false)
      return
    }

    if (typeof window === "undefined" || !inlineRegionRef.current) {
      return
    }

    const inlineTop =
      window.scrollY + inlineRegionRef.current.getBoundingClientRect().top
    const shouldShowTray = window.scrollY + window.innerHeight < inlineTop

    setIsTrayVisible(shouldShowTray)
  }, [inlineRegionRef, trayEnabled])

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    syncVisibility()

    window.addEventListener("load", syncVisibility)
    window.addEventListener("resize", syncVisibility)
    window.addEventListener("scroll", syncVisibility, { passive: true })

    return () => {
      window.removeEventListener("load", syncVisibility)
      window.removeEventListener("resize", syncVisibility)
      window.removeEventListener("scroll", syncVisibility)
    }
  }, [syncVisibility])

  return { isTrayVisible, syncVisibility }
}

export const ConsumerIsi: React.FC = () => {
  const closeButtonRef = React.useRef<HTMLButtonElement>(null)
  const inlineRegionRef = React.useRef<HTMLElement>(null)
  const sheetRef = React.useRef<HTMLDivElement>(null)
  const trayButtonRef = React.useRef<HTMLButtonElement>(null)
  const [isSheetMounted, setIsSheetMounted] = React.useState(false)
  const [isSheetOpen, setIsSheetOpen] = React.useState(false)
  const { isTrayVisible, syncVisibility } = useIsiTrayVisibility(
    inlineRegionRef,
    !isSheetMounted,
  )

  const handleOpen = React.useCallback(() => {
    setIsSheetMounted(true)

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        setIsSheetOpen(true)
      })
    } else {
      setIsSheetOpen(true)
    }
  }, [])

  const handleClose = React.useCallback(() => {
    setIsSheetOpen(false)
  }, [])

  React.useEffect(() => {
    if (!isSheetMounted) {
      return
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isSheetMounted])

  React.useEffect(() => {
    if (!isSheetMounted) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        handleClose()
        return
      }

      if (event.key !== "Tab") {
        return
      }

      const focusableElements = Array.from(
        sheetRef.current?.querySelectorAll<HTMLElement>(
          [
            "a[href]",
            "button:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            "[tabindex]:not([tabindex='-1'])",
          ].join(","),
        ) ?? [],
      ).filter(element => {
        const style = window.getComputedStyle(element)
        const rect = element.getBoundingClientRect()

        return (
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          rect.width > 0 &&
          rect.height > 0
        )
      })

      if (focusableElements.length === 0) {
        event.preventDefault()
        sheetRef.current?.focus()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const activeElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
        return
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleClose, isSheetMounted])

  React.useEffect(() => {
    if (!isSheetMounted) {
      return
    }

    if (isSheetOpen) {
      closeButtonRef.current?.focus()
      return
    }

    const timeout = window.setTimeout(() => {
      setIsSheetMounted(false)
      trayButtonRef.current?.focus()
      syncVisibility()
    }, sheetTransitionMs)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [isSheetMounted, isSheetOpen, syncVisibility])

  return (
    <>
      {isTrayVisible ? (
        <IsiTrayShell
          onOpen={handleOpen}
          preview={<ConsumerIsiPreview />}
          ref={trayButtonRef}
        />
      ) : null}

      <IsiInlineShell ref={inlineRegionRef}>
        <ConsumerIsiContent />
      </IsiInlineShell>

      {isSheetMounted && typeof document !== "undefined"
        ? createPortal(
            <IsiSheetShell
              closeButtonRef={closeButtonRef}
              isOpen={isSheetOpen}
              onClose={handleClose}
              sheetRef={sheetRef}
            >
              <ConsumerIsiContent />
            </IsiSheetShell>,
            document.body,
          )
        : null}
    </>
  )
}
